import type { DesignPage } from '../../services/designService';
import type { Task } from '../../services/taskService';
import type { Diagram } from '../../store/services/architectureApi';
import type { AgentLog } from '../../store/services/planningRuntimeApi';
import type { RequirementsResponse } from '../../store/services/requirementsApi';
import type { UserFlow } from '../../store/services/userFlowApi';
import type { SupervisorButton } from '../../types/supervisorButtons';
import type { ArtifactShareRequest } from '../../utils/artifactSharing';
import { isPlanningTaskContractComplete } from '../../utils/phaseBuildPresentation';
import { isReadyDesignPage } from '../DesignPanelHelpers';

import { overviewTimestamp } from './overviewDateTime';

type ShareType = ArtifactShareRequest['artifactType'];
interface TimelineArtifact {
  kind?: string;
  cardType?: string;
  artifact_type?: string;
  created_at?: string;
  timestamp?: string;
}
export interface GeneratedArtifactShare {
  artifactType: ShareType;
  completedAt: number;
  itemId?: string;
  pageName?: string;
}
export interface GeneratedArtifactShares {
  artifacts: GeneratedArtifactShare[];
  designs: GeneratedArtifactShare[];
}
export interface GeneratedArtifactSnapshots {
  requirements?: RequirementsResponse;
  flows?: UserFlow[];
  diagrams?: Diagram[];
  pages?: DesignPage[];
  tasks?: Task[];
  pmStepStatus?: string | null;
  agentLogs?: AgentLog[];
  timeline?: TimelineArtifact[];
}

export const shareTypeForArtifact = (type?: string | null): ShareType | undefined => {
  if (type === 'srd' || type === 'requirements') return 'requirements';
  if (type === 'design_page_done') return 'design';
  if (type === 'design' || type === 'user-flow' || type === 'architecture' || type === 'tasks') return type;

  return undefined;
};

const latest = (values: Array<string | null | undefined>): number => Math.max(0, ...values.map(overviewTimestamp));
const newestFirst = (left: GeneratedArtifactShare, right: GeneratedArtifactShare): number => (
  right.completedAt - left.completedAt
);

const GENERATING_AGENTS: Partial<Record<ShareType, string>> = {
  requirements: 'System Requirements Agent',
  'user-flow': 'User Flow Planner',
  architecture: 'System Architect',
};

/** New writes during a run need completion evidence; an older saved artifact remains usable. */
// eslint-disable-next-line max-statements
export function isCompletedArtifactSnapshot(
  type: ShareType, timestamp: number, snapshot: GeneratedArtifactSnapshots,
): boolean {
  const logs = snapshot.agentLogs?.filter((log) => log.agent_type === GENERATING_AGENTS[type]) ?? [];
  const startedAt = latest(logs.filter((log) => log.action === 'started').map((log) => log.created_at));
  const completedAt = latest(logs.filter((log) => log.action === 'completed').map((log) => log.created_at));
  const savedAt = timestamp || completedAt;

  if (!startedAt || (savedAt > 0 && savedAt < startedAt)) return true;
  // A proposal only writes pending_content; the accepted document stays intact.
  if (type === 'requirements' && logs.some((log) => log.action === 'awaiting_review'
    && overviewTimestamp(log.created_at) >= startedAt)) return true;
  // Flow and architecture markers are emitted at completion, unlike the SRD start marker.
  const markerAt = type === 'requirements' ? 0 : latest((snapshot.timeline ?? [])
    .filter((item) => item.artifact_type === type)
    .map((item) => item.created_at || item.timestamp));

  return Math.max(completedAt, markerAt) >= startedAt;
}

/** The generated-artifact controller owns sharing, including historical overflow placement. */
export const withoutLegacyDesignShare = (
  buttons: SupervisorButton[], managedSharing: boolean,
): SupervisorButton[] => managedSharing ? buttons.filter((button) => (
  button.key !== 'share_design' && button.action !== 'open_share_modal'
)) : buttons;

/** A log announces work; loaded, usable data is the evidence that sharing is ready. */
// eslint-disable-next-line max-statements
export function generatedArtifactShares(snapshot: GeneratedArtifactSnapshots): GeneratedArtifactShares {
  const completedAt = (type: ShareType, value: number): number => value || latest(
    (snapshot.agentLogs ?? []).filter((log) => log.agent_type === GENERATING_AGENTS[type] && log.action === 'completed')
      .map((log) => log.created_at),
  ) || latest(
    (snapshot.timeline ?? []).filter((item) => shareTypeForArtifact(item.cardType || item.artifact_type) === type)
      .map((item) => item.created_at || item.timestamp),
  );
  const artifacts: GeneratedArtifactShare[] = [];
  const add = (artifactType: ShareType, timestamp: number) => {
    if (!isCompletedArtifactSnapshot(artifactType, timestamp, snapshot)) return;
    artifacts.unshift({ artifactType, completedAt: completedAt(artifactType, timestamp) });
  };
  const requirements = snapshot.requirements;

  // Hidden metadata alone is not a document that can be shared.
  if (requirements?.content.replace(/<!--[\s\S]*?-->/g, '').trim()) {
    add('requirements', latest([requirements.updated_at, requirements.created_at]));
  }
  const flows = snapshot.flows?.filter((flow) => flow.mermaid_content.trim()) ?? [];

  if (flows.length) add('user-flow', latest(flows.map((flow) => flow.updated_at || flow.created_at)));
  const designs = (snapshot.pages ?? []).filter(isReadyDesignPage).map((page) => ({
    artifactType: 'design' as const,
    completedAt: completedAt('design', overviewTimestamp(page.updated_at)),
    itemId: page.id,
    pageName: page.page_name,
  })).sort(newestFirst);

  // One finished secondary page is enough, even while another page is still running.
  if (designs.length) add('design', designs[0].completedAt);
  const diagrams = snapshot.diagrams?.filter((diagram) => diagram.content.trim()) ?? [];

  if (diagrams.length) add('architecture', latest(diagrams.map((diagram) => diagram.updated_at)));
  const tasks = snapshot.tasks?.filter((task) => task.task_type !== 'planning' && task.creation_source === 'system') ?? [];

  if (tasks.length && isPlanningTaskContractComplete(snapshot.pmStepStatus)) {
    // Status/assignee edits update updated_at, but do not generate a new task plan.
    add('tasks', latest(tasks.map((task) => task.created_at)));
  }

  return { artifacts: artifacts.sort(newestFirst), designs };
}

export interface PresentedArtifactShare extends GeneratedArtifactShare {
  placement: 'pinned' | 'secondary';
}

/** Current controls carry every generated category; history only shares its own artifact. */
export function artifactSharesForBar(
  state: GeneratedArtifactShares,
  { current, artifactType, designPages }: {
    current: boolean;
    artifactType?: string | null;
    designPages?: string[];
  },
): PresentedArtifactShare[] {
  const type = shareTypeForArtifact(artifactType);
  const matchingPages = designPages?.map((page) => page.trim().toLocaleLowerCase());
  const historicalDesign = state.designs.find((page) => (
    matchingPages?.includes(page.pageName!.trim().toLocaleLowerCase())
  ));
  const historical = type === 'design' && matchingPages?.length
    ? (historicalDesign ? [historicalDesign] : [])
    : state.artifacts.filter((artifact) => artifact.artifactType === type);
  const latestArtifact = state.artifacts[0];

  return (current ? state.artifacts : historical).map((artifact) => ({
    ...artifact,
    placement: artifact.artifactType === latestArtifact?.artifactType
      && artifact.completedAt === latestArtifact.completedAt ? 'pinned' : 'secondary',
  }));
}
